'use client';

import { moneyFormatter } from '@/components/girl/utils';
import attendantFrontend from '@bells/api/attendants/frontend';
import geoContract from '@bells/api/geo/frontend';
import { cn } from '@bells/core/lib/utils';
import { generateRestClient } from '@bells/core/rest/react';
import {
  attendantTravelCostsCalculatorPrice,
  attendantTravelCostsCalculatorPriceNotice,
  attendantTravelCostsCalculatorTitle,
  crudNotFound,
  homeFaqExplore,
  locationSearchPlaceholder,
} from '@bells/i18n';
import { MapBoxFeature } from '@bells/schema/models/mapbox';
import { Button } from '@bells/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@bells/ui/components/command';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@bells/ui/components/drawer';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@bells/ui/components/popover';
import { compact, isEmpty } from 'lodash';
import { Calculator, Hotel, Loader2, MapPin } from 'lucide-react';
import { useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useMediaQuery } from 'usehooks-ts';
import { FlagIcon } from '@bells/ui/components/flag-icon';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@bells/ui/components/card';

const systemRestClient = generateRestClient(geoContract);
const systemDistanceClient = generateRestClient(attendantFrontend);

const Result = ({
  data,
  isLoading,
  term,
  setAddress,
}: {
  data: MapBoxFeature[];
  isLoading: boolean;
  term?: String;
  setAddress: (address: MapBoxFeature) => void;
}) => {
  return (
    <CommandList className='h-full max-h-max bg-transparent'>
      <CommandEmpty>
        {!isEmpty(term) && data?.length === 0 && !isLoading && (
          <div className='flex items-center justify-center py-4'>
            {crudNotFound()}
          </div>
        )}
      </CommandEmpty>

      <CommandGroup className={cn('relative h-auto', { hidden: !isLoading })}>
        <div className='relative flex items-center justify-center gap-2 px-2 py-2 uppercase text-muted-foreground'>
          <Loader2 className='h-8 w-8 animate-spin' />
        </div>
      </CommandGroup>
      <CommandGroup className={cn({ hidden: !data || data.length === 0 })}>
        {(data ?? []).map((item, index) => {
          return (
            <CommandItem
              value={`${
                item.properties.full_address ?? item.properties.place_formatted
              }`}
              onSelect={() => {
                setAddress(item);
              }}
              key={`search-${index}`}
              onMouseDown={(e) => e.preventDefault()}
            >
              <div className='flex w-full items-center gap-2'>
                <div className='flex w-full flex-col justify-between gap-1'>
                  <div className='flex w-full flex-1 items-center gap-2 text-sm font-medium'>
                    {item.properties.context?.country?.country_code && (
                      <FlagIcon
                        countryCode={item.properties.context.country.country_code.toUpperCase()}
                        className='h-4 w-4'
                      ></FlagIcon>
                    )}
                    {item.properties.name}
                  </div>

                  <div className='w-full text-sm text-muted-foreground'>
                    {item.properties.full_address ??
                      item.properties.place_formatted}
                  </div>
                </div>

                <div className='flex h-8 w-8 items-center justify-center'>
                  {item.properties.feature_type === 'poi' ? (
                    <Hotel></Hotel>
                  ) : (
                    <MapPin></MapPin>
                  )}
                </div>
              </div>
            </CommandItem>
          );
        })}
      </CommandGroup>
    </CommandList>
  );
};

export default function TravelCostCalculator({ slug }: { slug?: string }) {
  const [open, setOpen] = useState(false);

  const isDesktop = useMediaQuery('(min-width: 768px)');

  const [searchInput, setSearchInput] = useState<string>();
  const [debouncedSearchInput] = useDebounce(searchInput, 150);

  const [address, setAddress] = useState<MapBoxFeature | undefined>(undefined);
  const { data: travelDistanceData, isLoading: isLoadingCalculate } =
    systemDistanceClient.travelCosts.useQuery({
      queryKey: [
        'resolve-coordinates',
        address?.properties.coordinates.latitude ?? 0,
        address?.properties.coordinates.longitude ?? 0,
        slug,
      ],
      queryData: {
        query: {
          lat: address?.properties.coordinates.latitude ?? 0,
          lng: address?.properties.coordinates.longitude ?? 0,
        },
        params: { slug: slug ?? '' },
      },
      enabled:
        address !== undefined &&
        address?.properties.coordinates.latitude !== undefined &&
        address?.properties.coordinates.longitude !== undefined &&
        slug !== undefined,
      select: (df) => df.body,
    });

  const { data, isLoading } = systemRestClient.suggestForward.useQuery({
    queryKey: ['location-search', debouncedSearchInput],
    queryData: {
      query: {
        term: debouncedSearchInput ?? '',
        countries: ['de', 'at', 'ch', 'nl', 'fr', 'be', 'lu', 'it'],
        types: ['poi', 'postcode', 'city', 'street', 'address', 'place'],
        pois: ['hotel', 'hotel_bar', 'hostel'],
      },
    },
    enabled:
      debouncedSearchInput &&
      debouncedSearchInput.length > 1 &&
      !isLoadingCalculate
        ? true
        : false,
    select: (df) => df.body,
  });

  return (
    <Card className='m-0 w-full border-0 bg-transparent p-0 shadow-none'>
      <CardHeader className='px-0 pt-0'>
        <h3 className='flex items-center font-semibold leading-none tracking-tight'>
          <Calculator className='mr-1 size-4'></Calculator>
          {attendantTravelCostsCalculatorTitle()}
        </h3>
      </CardHeader>
      <CardContent className='flex w-full flex-col space-y-4 px-0'>
        {isDesktop ? (
          <Popover open={open} onOpenChange={setOpen}>
            <Command className='bg-transparent' shouldFilter={false}>
              <PopoverAnchor asChild>
                <div className='w-full rounded-xl border border-input'>
                  <CommandInput
                    withBorder={false}
                    value={searchInput}
                    className='bg-transparent'
                    onKeyDown={(e) =>
                      setOpen(!isEmpty(searchInput) && e.key !== 'Escape')
                    }
                    onMouseDown={() =>
                      setOpen((open) => !!searchInput || !open)
                    }
                    onFocus={() => setOpen(!isEmpty(searchInput))}
                    onValueChange={(val) => setSearchInput(val)}
                    placeholder={locationSearchPlaceholder()}
                  />
                </div>
              </PopoverAnchor>
              {!open && <CommandList aria-hidden='true' className='hidden' />}
              <PopoverContent
                onOpenAutoFocus={(e) => e.preventDefault()}
                onInteractOutside={(e) => {
                  if (
                    e.target instanceof Element &&
                    e.target.hasAttribute('cmdk-input')
                  ) {
                    e.preventDefault();
                  }
                }}
                className='w-[--radix-popover-trigger-width] p-0'
              >
                <Result
                  setAddress={(val) => {
                    setAddress(val);
                    setOpen(false);
                    setSearchInput(
                      val.properties.full_address
                        ? val.properties.full_address
                        : compact([
                            val.properties.name,
                            val.properties.place_formatted,
                          ]).join(', ')
                    );
                  }}
                  term={searchInput}
                  data={data ?? []}
                  isLoading={isLoading}
                ></Result>
              </PopoverContent>
            </Command>
          </Popover>
        ) : (
          <Drawer repositionInputs={false} open={open} onOpenChange={setOpen}>
            <DrawerTrigger asChild>
              <div className='w-full text-center'>
                <Button
                  variant={'outline'}
                  className='justify-left w-full items-start'
                >
                  <MapPin></MapPin>
                  {locationSearchPlaceholder()}
                </Button>
              </div>
            </DrawerTrigger>
            <DrawerContent className='flex h-[75vh] w-full flex-col'>
              <DrawerHeader>
                <DrawerTitle>
                  {attendantTravelCostsCalculatorTitle()}
                </DrawerTitle>
              </DrawerHeader>
              <Command shouldFilter={false} className='bg-transparent'>
                <CommandInput
                  value={searchInput}
                  className='font-medium'
                  onValueChange={(val) => setSearchInput(val)}
                  placeholder={homeFaqExplore()}
                />
                <Result
                  setAddress={(val) => {
                    setAddress(val);
                    setOpen(false);
                    setSearchInput(
                      val.properties.full_address
                        ? val.properties.full_address
                        : compact([
                            val.properties.name,
                            val.properties.place_formatted,
                          ]).join(', ')
                    );
                  }}
                  term={searchInput}
                  data={data ?? []}
                  isLoading={isLoading}
                ></Result>
              </Command>
            </DrawerContent>
          </Drawer>
        )}
        {isLoadingCalculate && (
          <div className='relative flex items-center justify-center gap-2 px-2 py-2 uppercase text-muted-foreground'>
            <Loader2 className='h-8 w-8 animate-spin' />
          </div>
        )}

        {travelDistanceData && (
          <div className='flex flex-wrap items-center justify-between'>
            <div className='flex items-center gap-2'>
              <span className='font-bold'>
                {attendantTravelCostsCalculatorPrice()}
              </span>
              <span className='font-bold'>
                {moneyFormatter.format(travelDistanceData.costs)}
              </span>
            </div>
            <div className='text-sm text-muted-foreground'>
              {attendantTravelCostsCalculatorPriceNotice({
                distance: `${travelDistanceData.distance} km`,
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
